import { ArrowDown, ArrowUp, Copy, RefreshCw, Trash2, Wand2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, cx } from "../../../components/ui";
import { api } from "../../../lib/api";
import { ActionPicker, readAction } from "./ActionPicker";
import { LiveCheck, type LiveResult } from "./LiveCheck";
import { branchById, describeCheck, fnName, renderArchive, RUN_FLAGS, sameWait, sampleValues, suggestCheck, suggestSubmit, waitLabel, waitTargets, type WaitRef, type ActionKind, type Definition, type Field, type Issue, type Step, type ValueSource, type VerifyMethod } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";

function Section({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section className="border-t border-line pt-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** The selected step's editor (docs/phase3-plan.md §1). Every change goes straight into the draft. */
export function StepEditor({
  def,
  step,
  issues,
  onChange,
  onMove,
  onCopy,
  onDelete,
  onClose,
  onAddBranch,
  onSelectBranch,
}: {
  def: Definition;
  step: Step;
  issues: Issue[];
  onChange: (s: Step) => void;
  onMove: (dir: -1 | 1) => void;
  onCopy: () => void;
  onDelete: () => void;
  onClose: () => void;
  onAddBranch?: () => void;
  onSelectBranch?: (id: string) => void;
}) {
  const [kind, setKind] = useState<ActionKind | null>(null);
  const [reading, setReading] = useState(false);
  // A tenant extension checked on a connection (Landmark metadata): its parameter names stand in for Waypoint's.
  const [live, setLive] = useState<LiveResult | null>(null);
  const cls = step.infor?.class ?? "";
  const action = step.infor?.action ?? "";
  const ownAction = step.infor?.submit === "form" || step.infor?.submit === "query" ? action.split("_")[0] : action;

  useEffect(() => {
    setKind(null);
    setLive(null);
    if (!cls || !ownAction || cls === "AsyncActionRequest") return;
    setReading(true);
    readAction(def.product, cls, ownAction)
      .then(setKind)
      .finally(() => setReading(false));
  }, [cls, ownAction, def.product]);

  const set = (patch: Partial<Step>) => onChange({ ...step, ...patch });
  const fnNames = useFunctionNames(def);
  const setInfor = (patch: Partial<NonNullable<Step["infor"]>>) => set({ infor: { class: cls, action, submit: "direct", fields: [], ...step.infor, ...patch } });
  const fields = step.infor?.fields ?? [];
  const setField = (i: number, f: Field) => setInfor({ fields: fields.map((x, j) => (j === i ? f : x)) });
  const params = kind?.parameters ?? (live?.actionFound ? live.parameters.map((name) => ({ name }) as NonNullable<ActionKind["parameters"]>[number]) : []);
  const param = (name: string) => params.find((p) => p.name === name.split(/[.[]/)[0]);
  const missing = params.filter((p) => !fields.some((f) => f.name.split(/[.[]/)[0] === p.name));
  const keyMissing = (kind?.declared === "Instance" ? kind.keys ?? [] : []).filter((k) => !fields.some((f) => f.name === k || f.name === `Prm${k}`));
  const myIssues = issues.filter((i) => i.where === `step ${step.id}`);

  /** A new action: Waypoint's suggestions for submit type, check and fields, and its findings in the instructions. */
  const pick = async (c: string, a: string) => {
    if (!a) return setInfor({ class: c, action: "" });
    const k = await readAction(def.product, c, a);
    const submit = suggestSubmit(k ?? undefined);
    const required = [...(k?.declared === "Instance" ? k.keys ?? [] : []), ...(k?.parameters ?? []).filter((p) => p.required === "always").map((p) => p.name)];
    const keep = fields.filter((f) => required.includes(f.name) || params.some((p) => p.name === f.name));
    const add = required.filter((n) => !keep.some((f) => f.name === n)).map((n): Field => ({ name: n, value: def.prompts.some((p) => p.code === n) ? { kind: "prompt", code: n } : { kind: "literal", value: "" } }));
    onChange({
      ...step,
      name: step.name.startsWith("New step") ? a.replace(/([a-z])([A-Z])/g, "$1 $2") : step.name,
      infor: { class: c, action: a, submit, fields: [...keep, ...add] },
      verify: suggestCheck(c, a, submit, k ?? undefined),
      ...(k && k.kind !== "unknown" ? { verifiedBy: `waypoint@${k.cu ?? ""}` } : { verifiedBy: undefined }),
      instructions: k && k.kind !== "unknown" ? summary(c, a, k) : step.instructions,
    });
  };

  return (
    <aside className="rounded-xl border border-line bg-surface p-4 space-y-4 text-sm" aria-label={`Edit step ${step.name}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted font-mono">{step.id}</div>
          <h2 className="font-display font-semibold text-ink text-lg">{step.name || "Step"}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" title="Move up" aria-label="Move up" onClick={() => onMove(-1)}>
            <ArrowUp className="size-4" />
          </Button>
          <Button variant="ghost" title="Move down" aria-label="Move down" onClick={() => onMove(1)}>
            <ArrowDown className="size-4" />
          </Button>
          <Button variant="ghost" title="Copy" aria-label="Copy step" onClick={onCopy}>
            <Copy className="size-4" />
          </Button>
          <Button variant="ghost" title="Delete" aria-label="Delete step" onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="ghost" title="Close" aria-label="Close editor" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {myIssues.map((i) => (
        <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.level === "error" ? "Fix: " : "⚠ "}
          {i.message}
        </p>
      ))}

      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={input} value={step.name} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label>
          <span className="text-ink-2 font-medium">Kind</span>
          <select className={input} value={step.kind} onChange={(e) => set(kindChange(def, step, e.target.value as Step["kind"]))}>
            <option value="job">Infor action</option>
            <option value="report">Report request</option>
            <option value="manual">Manual (nothing sent)</option>
            <option value="compass">Compass SQL query</option>
            <option value="radar">RADAR report</option>
          </select>
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={step.breakAfter} onChange={(e) => set({ breakAfter: e.target.checked })} />
          <span>Pause for review after this step</span>
        </label>
        <div className="col-span-2">
          <span className="text-ink-2 font-medium">Only run when</span>
          <div className="flex gap-2 mt-1">
            <select className={small} value={step.when?.prompt ?? ""} onChange={(e) => set({ when: e.target.value ? { prompt: e.target.value, equals: step.when?.equals ?? "" } : undefined })}>
              <option value="">Always run</option>
              {def.prompts.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
            {step.when && (
              <>
                <span className="self-center">=</span>
                {def.prompts.find((p) => p.code === step.when!.prompt)?.options ? (
                  <select className={small} value={step.when.equals} onChange={(e) => set({ when: { ...step.when!, equals: e.target.value } })}>
                    <option value="">Choose…</option>
                    {def.prompts.find((p) => p.code === step.when!.prompt)!.options!.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input className={small} value={step.when.equals} onChange={(e) => set({ when: { ...step.when!, equals: e.target.value } })} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {step.kind === "compass" && <CompassSection def={def} step={step} onChange={set} />}
      {step.kind === "radar" && <RadarSection def={def} step={step} onChange={set} />}

      {inforKind(step) && (
        <Section
          title="Infor action"
          aside={
            kind && (
              <button type="button" className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline" onClick={() => readAction(def.product, cls, ownAction, true).then(setKind)}>
                <RefreshCw className="size-3" aria-hidden /> Read again
              </button>
            )
          }
        >
          <ActionPicker line={def.product} cls={cls} action={action} onPick={(c, a) => void pick(c, a)} />
          {reading && <p className="text-xs text-muted">Reading {cls}.{ownAction} in Infor's delivered source…</p>}
          {kind && (
            <div className={cx("rounded-lg p-2 text-xs", kind.kind === "unknown" ? "bg-warn-soft text-ink-2" : "bg-surface-2 text-ink-2")}>
              {kind.kind === "unknown"
                ? `Not in delivered source (${kind.note && !/delivered source/.test(kind.note) ? kind.note : "it may be a tenant extension or an inherited action"}): try it on a connection.`
                : `${kind.declared ?? ""} action, ${kind.kind === "plain" ? "runs inside the call" : kind.kind === "background" ? "runs in background" : `fans out, finishing with ${kind.finishing?.action}`}${kind.background?.length ? `; starts ${kind.background.map((b) => b.action).join(", ")}` : ""}.`}
              {kind.restricted && <span className="block mt-0.5">Restricted: not on Infor's own screens, but the API can still run it.</span>}
              {kind.source && <span className="block text-muted font-mono mt-0.5">{kind.source}</span>}
            </div>
          )}
          {kind?.kind === "unknown" && (
            <>
              {step.verifiedBy?.startsWith("live:") && !live && <p className="text-xs text-muted">Checked in Infor: {step.verifiedBy.slice(5)}</p>}
              <LiveCheck
                product={def.product}
                cls={cls}
                action={ownAction}
                onChecked={(r, verifiedBy) => {
                  setLive(r);
                  if (verifiedBy) set({ verifiedBy, instructions: [step.instructions?.trim(), `${cls}.${ownAction} checked in Infor on ${r.connection} (${r.checkedAt.slice(0, 10)}): a tenant extension${r.parameters.length ? ` with parameters ${r.parameters.join(", ")}` : ""}.`].filter(Boolean).join("\n") });
                }}
              />
            </>
          )}
          <label className="block">
            <span className="text-ink-2 font-medium">Send as</span>
            <select className={input} value={step.infor?.submit ?? "direct"} onChange={(e) => setInfor({ submit: e.target.value as NonNullable<Step["infor"]>["submit"] })}>
              <option value="async">Background request (followed by its own request)</option>
              <option value="direct">Action (PUT)</option>
              <option value="form">Form operation (POST)</option>
              <option value="query">ldrest operation (GET, a write)</option>
            </select>
          </label>
        </Section>
      )}

      {inforKind(step) && step.infor && (
        <Section title={`Fields (${fields.length})`}>
          {fields.map((f, i) => {
            const p = param(f.name);
            const key = kind?.keys?.includes(f.name);
            return (
              <div key={i} className="rounded-lg border border-line p-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <input className={cx(small, "font-mono flex-1")} value={f.name} onChange={(e) => setField(i, { ...f, name: e.target.value.replace(/[^A-Za-z0-9_.[\]]/g, "") })} aria-label="Field name" />
                  <button type="button" className="text-muted hover:text-bad" aria-label={`Remove ${f.name}`} onClick={() => setInfor({ fields: fields.filter((_, j) => j !== i) })}>
                    <X className="size-4" />
                  </button>
                </div>
                <div className="text-xs text-muted">
                  {key ? "Record key" : p ? [p.type ?? "class field", p.required === "always" ? "required" : p.required === "conditional" ? "required in some cases" : "", p.default ? `default ${p.default}` : "", p.label].filter(Boolean).join(" · ") : kind?.parameters ? <span className="text-warn">Not a declared parameter of {cls}.{ownAction}</span> : ""}
                </div>
                <ValueEditor def={def} value={f.value} states={p?.states} rowArray={step.repeat?.array} onChange={(v) => setField(i, { ...f, value: v })} />
              </div>
            );
          })}
          <div className="flex flex-wrap gap-2">
            {[...keyMissing.map((n) => ({ name: n, why: "key" })), ...missing.map((p) => ({ name: p.name, why: p.required === "always" ? "required" : "" }))].slice(0, 12).map((p) => (
              <button key={p.name} type="button" className={cx("text-xs rounded-full border px-2 py-0.5 hover:bg-surface-2", p.why ? "border-warn text-warn" : "border-line text-ink-2")} onClick={() => setInfor({ fields: [...fields, { name: p.name, value: def.prompts.some((x) => x.code === p.name) ? { kind: "prompt", code: p.name } : { kind: "literal", value: "" } }] })}>
                + {p.name}
                {p.why && ` (${p.why})`}
              </button>
            ))}
            <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={() => setInfor({ fields: [...fields, { name: "", value: { kind: "literal", value: "" } }] })}>
              + Other field
            </button>
          </div>
        </Section>
      )}

      {(inforKind(step) || step.kind === "compass" || step.kind === "radar") && (
        <Section
          title="Done when"
          aside={
            step.infor && (
              <button type="button" className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline" onClick={() => set({ verify: suggestCheck(cls, action, step.infor!.submit, kind ?? undefined) })}>
                <Wand2 className="size-3" aria-hidden /> Use Waypoint's suggestion
              </button>
            )
          }
        >
          {inforKind(step) ? <CheckEditor value={step.verify} cls={cls} action={ownAction} onChange={(verify) => set({ verify })} functions={fnNames} /> : <p className="text-xs text-muted">{describeCheck(step.verify)}</p>}
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-ink-2 font-medium">Check every (seconds)</span>
              <input className={input} type="number" min={5} max={900} value={step.pollSeconds} onChange={(e) => set({ pollSeconds: Number(e.target.value) })} />
            </label>
            <label>
              <span className="text-ink-2 font-medium">Up to (checks)</span>
              <input className={input} type="number" min={1} value={step.maxChecks} onChange={(e) => set({ maxChecks: Number(e.target.value) })} />
            </label>
          </div>
          <p className="text-xs text-muted">Time limit about {Math.round((step.pollSeconds * step.maxChecks) / 60)} minutes.</p>
        </Section>
      )}

      {inforKind(step) && <ReportsEditor def={def} step={step} onChange={set} />}

      {(kind?.rerun || fields.some((f) => /^(Prm)?\w*Rerun\w*$/.test(f.name))) && (
        <Section title="Rerun">
          <label className="block">
            <span className="text-ink-2 font-medium">Default for runs</span>
            <select className={input} value={step.rerunDefault ?? "auto"} onChange={(e) => set({ rerunDefault: e.target.value as Step["rerunDefault"] })}>
              <option value="auto">Auto: rerun only if it already ran this cycle</option>
              <option value="yes">Yes: always send {kind?.rerun?.field ?? "Rerun"} = true</option>
              <option value="no">No: always send false</option>
            </select>
          </label>
          {kind?.rerun?.required && <p className="text-xs text-muted">Infor refuses a second run without it once {kind.rerun.flag} isn't 0.</p>}
        </Section>
      )}

      {(inforKind(step) || step.preFunctions?.length || step.postFunctions?.length) && <FunctionsSection def={def} step={step} onChange={set} />}

      {(inforKind(step) || step.repeat || step.loop) && <RepeatSection def={def} step={step} onChange={set} />}

      <FlowSection def={def} step={step} onChange={set} onAddBranch={onAddBranch} onSelectBranch={onSelectBranch} />

      <Section title="Instructions">
        <textarea className="w-full min-h-20 rounded-lg border border-line bg-surface p-2 text-sm" value={step.instructions ?? ""} onChange={(e) => set({ instructions: e.target.value })} />
        {step.verifiedBy && <p className="text-xs text-muted">Checked: {step.verifiedBy}</p>}
      </Section>
    </aside>
  );
}

/**
 * Reports after the step completes (each a ReportDocument request from a template), the report a report step makes
 * itself, and where finished reports are archived: DOWNLOADS/<folder><file>, with {{job}} and {{prompt:x|yyyymmdd}}.
 */
function ReportsEditor({ def, step, onChange }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void }) {
  const names = [...(step.ownReport ? [step.ownReport] : []), ...step.reports.map((r) => r.name)].filter(Boolean);
  const samples = sampleValues(def);
  const dateFolder = def.runDatePrompt ? `{{prompt:${def.runDatePrompt}|yyyymmdd}}/` : "";
  const setReport = (i: number, patch: Partial<Step["reports"][number]>) => onChange({ reports: step.reports.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const setArchive = (i: number, patch: Partial<Step["archives"][number]>) => onChange({ archives: step.archives.map((a, j) => (j === i ? { ...a, ...patch } : a)) });
  // A report's rename follows into its archives.
  const renameReport = (i: number, name: string) => {
    const old = step.reports[i].name;
    onChange({ reports: step.reports.map((r, j) => (j === i ? { ...r, name } : r)), archives: step.archives.map((a) => (a.report === old ? { ...a, report: name } : a)) });
  };
  return (
    <Section title={`Reports (${names.length})`}>
      {step.kind === "report" && (
        <label className="block">
          <span className="text-ink-2 font-medium">Report this step makes</span>
          <input
            className={input}
            value={step.ownReport ?? ""}
            onChange={(e) => {
              const old = step.ownReport;
              const v = e.target.value || undefined;
              onChange({ ownReport: v, archives: step.archives.map((a) => (old && a.report === old ? { ...a, report: v ?? "" } : a)) });
            }}
            placeholder="e.g. Batch"
          />
        </label>
      )}
      {step.reports.map((r, i) => (
        <div key={i} className="rounded-lg border border-line p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <input className={cx(small, "flex-1")} value={r.name} onChange={(e) => renameReport(i, e.target.value)} aria-label="Report name" placeholder="Report name" />
            <button type="button" className="text-muted hover:text-bad" aria-label={`Remove report ${r.name}`} onClick={() => onChange({ reports: step.reports.filter((_, j) => j !== i), archives: step.archives.filter((a) => a.report !== r.name) })}>
              <X className="size-4" />
            </button>
          </div>
          <select className={cx(small, "w-full font-mono")} value={r.template} onChange={(e) => setReport(i, { template: e.target.value })} aria-label={`Template for ${r.name}`}>
            {!def.templates.some((t) => t.id === r.template) && <option value={r.template}>{r.template || "Choose a template…"}</option>}
            {def.templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted">Requested as “{`<job> ${r.name}`}” once the step completes.</p>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" disabled={!def.templates.length} title={def.templates.length ? undefined : "Add a report template first"} onClick={() => onChange({ reports: [...step.reports, { name: `Report ${step.reports.length + 1}`, template: def.templates[0]?.id ?? "" }] })}>
          + Report
        </button>
        {names.length > 0 && (
          <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={() => onChange({ archives: [...step.archives, { report: names[0], folder: dateFolder, file: `{{job}}/${names[0].replace(/\s+/g, "")}.csv` }] })}>
            + Archive a report
          </button>
        )}
      </div>
      {step.archives.map((a, i) => (
        <div key={i} className="rounded-lg border border-line p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-2">Archive</span>
            <select className={cx(small, "flex-1")} value={a.report} onChange={(e) => setArchive(i, { report: e.target.value })} aria-label="Report to archive">
              {!names.includes(a.report) && <option value={a.report}>{a.report || "Choose…"}</option>}
              {names.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button type="button" className="text-muted hover:text-bad" aria-label={`Remove archive of ${a.report}`} onClick={() => onChange({ archives: step.archives.filter((_, j) => j !== i) })}>
              <X className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className={cx(small, "font-mono text-xs")} value={a.folder} onChange={(e) => setArchive(i, { folder: e.target.value })} aria-label="Folder" placeholder="Folder" />
            <input className={cx(small, "font-mono text-xs")} value={a.file} onChange={(e) => setArchive(i, { file: e.target.value })} aria-label="File" placeholder="File" />
          </div>
          <p className="text-xs text-muted font-mono break-all">e.g. {renderArchive(a.folder, a.file, samples, "QH3")}</p>
        </div>
      ))}
      {step.archives.length > 0 && <p className="text-xs text-muted">{"{{job}}"} is the step's job name; {"{{prompt:code}}"} or {"{{prompt:code|yyyymmdd}}"} a prompt's value.</p>}
    </Section>
  );
}

function summary(cls: string, action: string, k: ActionKind) {
  const req = (k.parameters ?? []).filter((p) => p.required === "always").map((p) => p.name);
  return [
    `${cls}.${action} (${k.declared ?? ""} action, ${k.kind === "plain" ? "runs inside the call" : k.kind === "background" ? "runs in background" : `fans out, finishing with ${k.finishing?.action}`}).`,
    k.keys?.length && k.declared === "Instance" ? `Record key: ${k.keys.join(", ")}.` : "",
    req.length ? `Required: ${req.join(", ")}.` : "",
    k.background?.length ? `Starts in background: ${k.background.map((b) => b.action).join(", ")}.` : "",
    k.rerun ? `Rerun parameter: ${k.rerun.field}${k.rerun.flag ? ` (required once ${k.rerun.flag} isn't 0)` : ""}.` : "",
    `From Waypoint, ${k.source ?? ""} (CU ${k.cu ?? "?"}).`,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Steps that send an Infor action (and so have fields, reports and a choice of check). */
const inforKind = (s: Step) => s.kind === "job" || s.kind === "report" || s.kind === "none";

const allSteps = (def: Definition) => [...def.trunk, ...(def.branches ?? []).flatMap((b) => b.steps)];

/** Switching kind: Compass SQL and RADAR steps carry their own settings and check, and no Infor action. */
function kindChange(def: Definition, step: Step, kind: Step["kind"]): Partial<Step> {
  if (kind === "compass") return { kind, infor: undefined, radar: undefined, reports: [], archives: [], verify: { method: "compassQuery" }, compass: step.compass ?? { sql: "", version: 0, params: {} } };
  if (kind === "radar") {
    const src = allSteps(def).find((s) => s.kind === "compass" && s.id !== step.id);
    return { kind, infor: undefined, compass: undefined, reports: [], archives: [], verify: { method: "radarJob" }, radar: step.radar ?? { op: "register", queryFrom: src ? `step:${src.id}` : "", pushToInfor: false } };
  }
  const was = step.kind === "compass" || step.kind === "radar";
  return { kind, ...(was ? { compass: undefined, radar: undefined, verify: { method: "manual" } } : {}) };
}

interface SqlEntry {
  id: string;
  name: string;
  description: string;
  current: number;
  params: { name: string; label: string; type: "text" | "number" | "date"; required: boolean; default?: string }[];
}

/** A Compass SQL step (design §15.9.2): pick a SQL library statement, then give each of its parameters a value. */
function CompassSection({ def, step, onChange }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void }) {
  const [entries, setEntries] = useState<SqlEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ entries: SqlEntry[] }>("/apps/radar/sql")
      .then((r) => setEntries(r.entries))
      .catch((e) => setError((e as Error).message));
  }, []);
  const c = step.compass ?? { sql: "", version: 0, params: {} };
  const entry = entries?.find((e) => e.id === c.sql);
  // A parameter with a prompt of the same name gets that prompt; otherwise its default (or blank).
  const pick = (e: SqlEntry) =>
    onChange({
      compass: {
        sql: e.id,
        version: e.current,
        params: Object.fromEntries(e.params.map((p) => [p.name, c.params[p.name] ?? (def.prompts.some((x) => x.code === p.name) ? { kind: "prompt" as const, code: p.name } : { kind: "literal" as const, value: p.default ?? "" })])),
      },
    });
  return (
    <Section title="Compass SQL" aside={entry && entry.current > c.version ? <button type="button" className="text-xs text-accent-text hover:underline" onClick={() => pick(entry)}>Use version {entry.current} (this step uses {c.version})</button> : undefined}>
      {error && <p className="text-xs text-bad">{error}</p>}
      <label className="block">
        <span className="text-ink-2 font-medium">SQL library statement</span>
        <select className={input} value={c.sql} onChange={(e) => { const x = entries?.find((y) => y.id === e.target.value); if (x) pick(x); }}>
          <option value="">{entries ? "Choose a statement…" : "Loading…"}</option>
          {(entries ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        {entry && <span className="block text-xs text-muted mt-1">{entry.description || "No description"} · version {c.version}</span>}
      </label>
      {entry && (
        <div className="space-y-2">
          <span className="text-ink-2 font-medium">Parameters</span>
          {entry.params.length === 0 && <p className="text-xs text-muted">This statement has no parameters.</p>}
          {entry.params.map((p) => (
            <div key={p.name} className="grid grid-cols-[10rem_1fr] gap-2 items-start">
              <div className="text-sm pt-1.5">
                {p.label}
                <span className="block text-xs text-muted">
                  {p.type}
                  {p.required ? ", required" : ""}
                  {p.default ? `, default ${p.default}` : ""}
                </span>
              </div>
              <ValueEditor def={def} value={c.params[p.name] ?? { kind: "literal", value: "" }} onChange={(v) => onChange({ compass: { ...c, params: { ...c.params, [p.name]: v } } })} />
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted">The step submits the statement to Compass with these values and keeps the query id; RADAR steps after it read the query's result.</p>
    </Section>
  );
}

/** A RADAR step (design §15.5): a TIMERECORD or REGISTER report (or the raw CSV) from a Compass SQL step's query. */
function RadarSection({ def, step, onChange }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void }) {
  const r = step.radar ?? { op: "register" as const, queryFrom: "" };
  const sources = allSteps(def).filter((s) => s.kind === "compass" && s.id !== step.id);
  const set = (patch: Partial<NonNullable<Step["radar"]>>) => onChange({ radar: { ...r, ...patch } });
  return (
    <Section title="RADAR">
      <div className="grid grid-cols-2 gap-3">
        <label>
          <span className="text-ink-2 font-medium">Produces</span>
          <select className={input} value={r.op} onChange={(e) => set({ op: e.target.value as NonNullable<Step["radar"]>["op"] })}>
            <option value="timerecord">Time record report (TIMERECORD)</option>
            <option value="register">Payroll register report (REGISTER)</option>
            <option value="compassExport">The query's raw CSV</option>
          </select>
        </label>
        <label>
          <span className="text-ink-2 font-medium">From the query of</span>
          <select className={input} value={r.queryFrom} onChange={(e) => set({ queryFrom: e.target.value })}>
            <option value="">{sources.length ? "Choose a Compass SQL step…" : "Add a Compass SQL step first"}</option>
            {sources.map((s) => (
              <option key={s.id} value={`step:${s.id}`}>
                {s.name} ({s.id})
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={r.pushToInfor === true} onChange={(e) => set({ pushToInfor: e.target.checked })} />
        <span>
          <span className="font-medium text-ink">Send to Infor Print Manager</span>
          <span className="block text-xs text-muted">Shared with the connection's service account, and optionally with a distribution group.</span>
        </span>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label>
          <span className="text-ink-2 font-medium">Report name</span>
          <input className={input} value={r.reportName ?? ""} onChange={(e) => set({ reportName: e.target.value || undefined })} placeholder="Optional" maxLength={200} />
        </label>
        {r.pushToInfor && (
          <label>
            <span className="text-ink-2 font-medium">Distribution group</span>
            <input className={input} value={r.distProfile ?? ""} onChange={(e) => set({ distProfile: e.target.value.trim() || undefined })} placeholder="e.g. EmployeeSnapshotGroup" maxLength={60} />
          </label>
        )}
      </div>
      <p className="text-xs text-muted">The run's Status page shows the RADAR job id; the report opens from RADAR.</p>
    </Section>
  );
}

function ValueEditor({ def, value, states, rowArray, onChange }: { def: Definition; value: ValueSource; states?: { name: string; value: string }[]; rowArray?: string; onChange: (v: ValueSource) => void }) {
  const arrays = def.arrays ?? [];
  const attrsOf = (name?: string) => arrays.find((a) => a.name === name)?.attrs ?? [];
  return (
    <div className="flex flex-wrap gap-2">
      <select
        className={small}
        value={value.kind}
        aria-label="Value comes from"
        onChange={(e) => {
          const k = e.target.value as ValueSource["kind"];
          onChange(
            k === "literal" ? { kind: "literal", value: "" }
            : k === "prompt" ? { kind: "prompt", code: def.prompts[0]?.code ?? "" }
            : k === "builtin" ? { kind: "builtin", name: "reportName" }
            : k === "array" ? { kind: "array" }
            : k === "arrayRows" ? { kind: "arrayRows", array: arrays[0]?.name ?? "" }
            : k === "function" ? { kind: "function", call: (def.functions ?? [])[0]?.key ?? "" }
            : { kind: "report", template: def.templates[0]?.id ?? "" },
          );
        }}
      >
        <option value="literal">Fixed value</option>
        <option value="prompt">Prompt</option>
        <option value="builtin">Built-in</option>
        <option value="report">Report template</option>
        {(rowArray || value.kind === "array") && <option value="array">This pass's array row</option>}
        {(arrays.length > 0 || value.kind === "arrayRows") && <option value="arrayRows">Every row of an array (name1..N)</option>}
        <option value="function">Function</option>
      </select>
      {value.kind === "function" && (
        <>
          <input className={cx(small, "font-mono flex-1 min-w-40")} list="field-function-names" value={value.call} onChange={(e) => onChange({ ...value, call: e.target.value })} placeholder="e.g. buildReportName() or buildWorkTitle(val)" aria-label="Function call" />
          <datalist id="field-function-names">
            {(def.functions ?? []).map((f) => (
              <option key={f.key} value={f.key} />
            ))}
          </datalist>
          <select
            className={small}
            aria-label="val is"
            value={value.prompt !== undefined ? "prompt" : "text"}
            onChange={(e) => onChange(e.target.value === "prompt" ? { kind: "function", call: value.call, prompt: def.prompts[0]?.code ?? "" } : { kind: "function", call: value.call })}
          >
            <option value="text">val =</option>
            <option value="prompt">val = prompt</option>
          </select>
          {value.prompt !== undefined ? (
            <select className={small} value={value.prompt} aria-label="Prompt passed as val" onChange={(e) => onChange({ ...value, prompt: e.target.value })}>
              {def.prompts.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          ) : (
            <input className={cx(small, "w-32")} value={value.value ?? ""} onChange={(e) => onChange({ kind: "function", call: value.call, ...(e.target.value ? { value: e.target.value } : {}) })} placeholder="(nothing)" aria-label="Text passed as val" />
          )}
        </>
      )}
      {value.kind === "array" && (
        <select className={small} value={value.attr ?? ""} aria-label="Row value or attribute" onChange={(e) => onChange({ kind: "array", ...(e.target.value ? { attr: e.target.value } : {}) })}>
          <option value="">The row's value</option>
          {attrsOf(rowArray).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      )}
      {value.kind === "arrayRows" && (
        <>
          <select className={small} value={value.array} aria-label="Array" onChange={(e) => onChange({ kind: "arrayRows", array: e.target.value })}>
            {arrays.map((a) => (
              <option key={a.name} value={a.name}>
                {a.name}
              </option>
            ))}
          </select>
          <select className={small} value={value.attr ?? ""} aria-label="Row value or attribute" onChange={(e) => onChange({ kind: "arrayRows", array: value.array, ...(e.target.value ? { attr: e.target.value } : {}) })}>
            <option value="">Each row's value</option>
            {attrsOf(value.array).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </>
      )}
      {value.kind === "literal" &&
        (states ? (
          <select className={small} value={value.value} onChange={(e) => onChange({ kind: "literal", value: e.target.value })}>
            <option value="">(blank)</option>
            {states.map((s) => (
              <option key={s.value} value={s.value}>
                {s.value} {s.name}
              </option>
            ))}
            {value.value && !states.some((s) => s.value === value.value) && <option value={value.value}>{value.value} (not a listed value)</option>}
          </select>
        ) : (
          <input className={cx(small, "flex-1 min-w-32")} value={value.value} onChange={(e) => onChange({ kind: "literal", value: e.target.value })} placeholder="(blank)" />
        ))}
      {value.kind === "prompt" && (
        <>
          <select className={small} value={value.code} onChange={(e) => onChange({ ...value, code: e.target.value })}>
            {def.prompts.map((p) => (
              <option key={p.code} value={p.code}>
                {p.label}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1 text-xs">
            <input type="checkbox" checked={value.format === "yyyymmdd"} onChange={(e) => onChange({ kind: "prompt", code: value.code, ...(e.target.checked ? { format: "yyyymmdd" as const } : {}) })} />
            as YYYYMMDD
          </label>
        </>
      )}
      {value.kind === "builtin" && (
        <>
          <select className={small} value={value.name} onChange={(e) => onChange({ kind: "builtin", name: e.target.value as "reportName" | "workTitle", arg: value.arg })}>
            <option value="reportName">Report name (job + report)</option>
            <option value="workTitle">Work title (text + time)</option>
          </select>
          {value.name === "workTitle" && <input className={small} value={value.arg ?? ""} onChange={(e) => onChange({ ...value, arg: e.target.value })} placeholder="Title text" />}
        </>
      )}
      {value.kind === "report" && (
        <select className={small} value={value.template} onChange={(e) => onChange({ kind: "report", template: e.target.value })}>
          {def.templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.id}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Repeat (docs/phase5-plan.md §3): run the step once for each row of an array (legacy multiple runs; a fixed list), or
 * loop up to N times while a function before sending picks the next item and ends the loop with skipJob() (the
 * BayCare pattern; a list that varies). Each pass is its own job; the break and branches follow the last pass.
 */
function RepeatSection({ def, step, onChange }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void }) {
  const arrays = def.arrays ?? [];
  const mode = step.repeat ? "rows" : step.loop ? "loop" : "once";
  const a = arrays.find((x) => x.name === step.repeat?.array);
  const w = step.repeat?.where;
  return (
    <Section title="Repeat">
      <select
        className={small}
        value={mode}
        aria-label="How often the step runs"
        onChange={(e) => {
          const m = e.target.value;
          onChange(m === "rows" ? { repeat: { array: arrays[0]?.name ?? "" }, loop: undefined } : m === "loop" ? { loop: { max: 10 }, repeat: undefined } : { repeat: undefined, loop: undefined });
        }}
      >
        <option value="once">Once</option>
        <option value="rows" disabled={!arrays.length && mode !== "rows"}>
          For each row of an array{arrays.length ? "" : " (add one in the Arrays tab)"}
        </option>
        <option value="loop">In a loop, until a function ends it</option>
      </select>
      {step.repeat && (
        <div className="flex flex-wrap gap-2 items-center">
          <select className={small} value={step.repeat.array} aria-label="Array" onChange={(e) => onChange({ repeat: { array: e.target.value } })}>
            {arrays.map((x) => (
              <option key={x.name} value={x.name}>
                {x.name} ({x.rows.length} rows)
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1 text-xs">
            <input type="checkbox" checked={!!w} onChange={(e) => onChange({ repeat: { array: step.repeat!.array, ...(e.target.checked ? { where: { attr: a?.attrs[0] ?? "", equals: "" } } : {}) } })} />
            only rows where
          </label>
          {w && (
            <>
              <select className={small} value={w.attr} aria-label="Attribute" onChange={(e) => onChange({ repeat: { ...step.repeat!, where: { ...w, attr: e.target.value } } })}>
                {(a?.attrs ?? []).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="text-xs">=</span>
              <input className={small} list="repeat-prompts" value={w.equals} aria-label="Equals a value or a prompt" placeholder="value or prompt" onChange={(e) => onChange({ repeat: { ...step.repeat!, where: { ...w, equals: e.target.value } } })} />
              <datalist id="repeat-prompts">
                {def.prompts.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label}
                  </option>
                ))}
              </datalist>
            </>
          )}
        </div>
      )}
      {step.loop && (
        <label className="inline-flex items-center gap-2">
          <span className="text-ink-2">At most</span>
          <input className={cx(small, "w-20")} type="number" min={1} max={200} value={step.loop.max} onChange={(e) => onChange({ loop: { max: Math.max(1, Math.min(200, Number(e.target.value) || 1)) } })} aria-label="Most passes" />
          <span className="text-ink-2">passes</span>
        </label>
      )}
      <p className="text-xs text-muted">
        {step.repeat
          ? "One job per row; fields can use this pass's row, and its functions see it as jobrun[jobcnt].arrayobj. The break and branches follow the last row."
          : step.loop
            ? "Each pass runs the functions before sending first: they pick the next item (e.g. into a prompt) and call skipJob() when there's nothing left, which ends the loop. The break and branches follow the loop."
            : "Runs once. Repeat it for each item of a fixed list (an array), or loop over a list that changes each period."}
      </p>
      {step.loop && !(step.preFunctions ?? []).length && <p className="text-xs text-bad">A loop needs a function before sending to end it.</p>}
    </Section>
  );
}

/** Names of the functions a step can call: the automation's own, then the library's (loaded once). */
let libNames: Promise<string[]> | null = null;
function useFunctionNames(def: Definition) {
  const [lib, setLib] = useState<string[]>([]);
  useEffect(() => {
    libNames ??= api<{ functions: { key: string }[] }>("/apps/ja/functions").then((r) => r.functions.map((f) => f.key), () => []);
    void libNames.then(setLib);
  }, []);
  return [...(def.functions ?? []).map((f) => f.key), ...lib.filter((k) => !(def.functions ?? []).some((f) => fnName(f.key) === fnName(k)))];
}

/**
 * The step's functions (docs/phase5-plan.md §3): run in the sandbox before the step is sent (legacy prefunction; the
 * chain carries on to the send with lib_popfunction(), skipJob() skips the step) and once it's done (postfunction).
 */
function FunctionsSection({ def, step, onChange }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void }) {
  const names = useFunctionNames(def);
  const list = (which: "preFunctions" | "postFunctions", title: string, help: string) => {
    const calls = step[which] ?? [];
    const set = (next: string[]) => onChange({ [which]: next.length ? next : undefined } as Partial<Step>);
    return (
      <div className="space-y-1.5">
        <span className="text-ink-2 font-medium">{title}</span>
        {calls.map((c, i) => (
          <div key={i} className="flex gap-2">
            <input className={cx(small, "font-mono flex-1")} list="step-function-names" value={c} onChange={(e) => set(calls.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`${title} ${i + 1}`} />
            <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label="Remove" onClick={() => set(calls.filter((_, j) => j !== i))}>
              <X className="size-4" />
            </button>
          </div>
        ))}
        <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={() => set([...calls, names[0] ?? ""])}>
          + Function
        </button>
        <p className="text-xs text-muted">{help}</p>
      </div>
    );
  };
  return (
    <Section title="Functions">
      <datalist id="step-function-names">
        {names.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      {list("preFunctions", "Before sending", "Run in order. Each calls lib_popfunction() to carry on; the step is sent after the last. skipJob() skips the step.")}
      {list("postFunctions", "After the step", "Run once the step is done. A problem here is a warning; the step stays done.")}
      {!names.length && <p className="text-xs text-muted">Add functions in the Functions tab first.</p>}
    </Section>
  );
}

function CheckEditor({ value, cls, action, onChange, functions }: { value: VerifyMethod; cls: string; action: string; onChange: (v: VerifyMethod) => void; functions?: string[] }) {
  return (
    <div className="space-y-2">
      <select
        className={input}
        value={value.method}
        onChange={(e) => {
          const m = e.target.value as VerifyMethod["method"];
          onChange(m === "paymentScheduleFlag" ? { method: m, flag: RUN_FLAGS.includes(action) ? action : RUN_FLAGS[0] } : m === "newRequest" ? { method: m, cls, action } : m === "replicationSet" ? { method: m, set: "" } : m === "custom" ? { method: m, function: "" } : ({ method: m } as VerifyMethod));
        }}
      >
        <option value="asyncActionGroup">Its own request's group completes</option>
        <option value="paymentScheduleFlag">A PaymentSchedule run flag settles</option>
        <option value="newRequest">The request it starts completes</option>
        <option value="pfiWorkunit">Its IPA work unit completes</option>
        <option value="manual">Done when Infor accepts it</option>
        <option value="replicationSet">A replication set finishes replicating</option>
        <option value="custom">A function says it's done</option>
      </select>
      {value.method === "custom" && (
        <>
          <input className={cx(small, "font-mono")} list="check-function-names" value={value.function} onChange={(e) => onChange({ method: "custom", function: e.target.value })} placeholder="Function, e.g. batchReleased()" aria-label="Check function" />
          <datalist id="check-function-names">
            {(functions ?? []).map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <p className="text-xs text-muted">Called every check with the check number. Return true when the step is done; false to check again; ErrorMsg() fails it.</p>
        </>
      )}
      {value.method === "replicationSet" && (
        <input className={cx(small, "font-mono")} value={value.set} onChange={(e) => onChange({ method: "replicationSet", set: e.target.value.trim() })} placeholder="Replication set, e.g. GHR_PYRL_ST" />
      )}
      {value.method === "paymentScheduleFlag" && (
        <select className={small} value={value.flag} onChange={(e) => onChange({ method: "paymentScheduleFlag", flag: e.target.value })}>
          {RUN_FLAGS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      )}
      {value.method === "newRequest" && (
        <div className="flex gap-2">
          <input className={cx(small, "font-mono flex-1")} value={value.cls} onChange={(e) => onChange({ ...value, cls: e.target.value })} placeholder="Class (blank = any)" />
          <input className={cx(small, "font-mono flex-1")} value={value.action} onChange={(e) => onChange({ ...value, action: e.target.value })} placeholder="Action" />
        </div>
      )}
      <p className="text-xs text-muted">{describeCheck(value)}</p>
    </div>
  );
}

/**
 * The step's place in the tree (docs/phase4-plan.md §4): the branches it starts, what it waits for before it sends,
 * how far its break reaches, and (a report step) whether its break waits for its own file.
 */
function FlowSection({ def, step, onChange, onAddBranch, onSelectBranch }: { def: Definition; step: Step; onChange: (patch: Partial<Step>) => void; onAddBranch?: () => void; onSelectBranch?: (id: string) => void }) {
  const targets = waitTargets(def, step.id);
  const waits = step.waitFor ?? [];
  const starts = (step.branches ?? []).map((id) => branchById(def, id)).filter((b): b is NonNullable<typeof b> => !!b);
  const toggle = (ref: WaitRef, on: boolean) => {
    const next = on ? [...waits, ref] : waits.filter((w) => !sameWait(w, ref));
    onChange({ waitFor: next.length ? next : undefined });
  };
  // Waits the designer can no longer pick (the target moved or went away): shown so they can be removed.
  const stale = waits.filter((w) => !targets.some((t) => sameWait(t.ref, w)));
  return (
    <Section title="Flow">
      <div className="space-y-1">
        <span className="text-ink-2 font-medium">Starts</span>
        {starts.length ? (
          <ul className="space-y-0.5">
            {starts.map((b) => (
              <li key={b.id}>
                <button type="button" className="text-accent-text hover:underline text-sm" onClick={() => onSelectBranch?.(b.id)}>
                  Branch {b.name}
                </button>
                <span className="text-xs text-muted"> · {b.critical ? "critical" : "non-critical"} · {b.steps.length} step{b.steps.length === 1 ? "" : "s"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted">No step branches. Its reports run as their own branches.</p>
        )}
        {onAddBranch && (
          <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={onAddBranch}>
            + Branch that starts after this step
          </button>
        )}
      </div>
      <div className="space-y-1">
        <span className="text-ink-2 font-medium">Waits for</span>
        {targets.length === 0 && stale.length === 0 ? (
          <p className="text-xs text-muted">Nothing started earlier to wait for.</p>
        ) : (
          <div className="max-h-48 overflow-y-auto rounded-lg border border-line p-2 space-y-1">
            {targets.map((t) => (
              <label key={JSON.stringify(t.ref)} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={waits.some((w) => sameWait(w, t.ref))} onChange={(e) => toggle(t.ref, e.target.checked)} />
                {t.label}
              </label>
            ))}
            {stale.map((w) => (
              <label key={JSON.stringify(w)} className="flex items-center gap-2 text-sm text-bad">
                <input type="checkbox" checked onChange={() => toggle(w, false)} />
                {waitLabel(def, w)} (no longer earlier than this step)
              </label>
            ))}
          </div>
        )}
        <p className="text-xs text-muted">Reports never hold the run: tick one here only if this step needs its file first.</p>
      </div>
      {step.breakAfter && (
        <label className="block">
          <span className="text-ink-2 font-medium">The break after it pauses</span>
          <select className={input} value={step.breakScope ?? "branch"} onChange={(e) => onChange({ breakScope: e.target.value === "run" ? "run" : undefined })}>
            <option value="branch">Only its own path (other branches carry on)</option>
            <option value="run">The whole run</option>
          </select>
        </label>
      )}
      {step.kind === "report" && step.breakAfter && (
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-1" checked={!!step.waitsForOwnReport} onChange={(e) => onChange({ waitsForOwnReport: e.target.checked || undefined })} />
          <span>
            Its break waits for its own report's file
            <span className="block text-xs text-muted">Otherwise the step is done when Infor accepts the request, and the break opens while the file is still being made.</span>
          </span>
        </label>
      )}
    </Section>
  );
}
